import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xArray, xObject, xReference, xString } from './XTypes.js';

import template from './XContent.html?raw';

import XScrollTranslator from './XScrollTranslator.js';
import { xCheckSystem_iOS } from './XUtil.js';

export class XContent
{
    static get type () { return 'XContent'; }

    #dom;
    #page;
    #site;

    #sections;

    #element;
    #elementMasterControlsPlay;
    #elementMasterControlsPlayIcon;
    #elementMasterControlsVolumeSlider;
    #elementMasterControlsVolumeLabel;
    #elementMasterControlsPrevious;
    #elementMasterControlsNext;

    #masterVolume = 0.5;

    #blockPlayback = null;
    #blockFocus = null;

    #scrollTranslator;

    constructor(dom, page, site)
    {
        this.#dom  = dom;
        this.#page = page;

        this.#element = this.#dom._element;

        const domTypes = environments.active.domTypes;
        this.#sections = this.#dom.sections._setCreator(() => model => new domTypes.XSection(model, this, page, site));

        const domMasterControls = this.#dom.masterControls;
        this.#elementMasterControlsPlay = domMasterControls.play._element;
        this.#elementMasterControlsPlayIcon = domMasterControls.play.icon._element;
        this.#elementMasterControlsPlay.addEventListener('click', () => {
            if(this.#blockPlayback)
            {
                this.#blockPlayback.stop();
            }
            else if(this.#blockFocus)
            {
                this.#blockFocus.play();
            }
        });

        this.#elementMasterControlsVolumeSlider = domMasterControls.volume.slider._element;
        this.#elementMasterControlsVolumeLabel = domMasterControls.volume.label._element;
        this.#elementMasterControlsVolumeSlider.addEventListener('input', () => {
            this.masterVolume = this.#elementMasterControlsVolumeSlider.value;
        });
        if(xCheckSystem_iOS())
        {
            domMasterControls.volume._element.classList.add('d-none');
        }

        this.#elementMasterControlsPrevious = domMasterControls.previous._element;
        this.#elementMasterControlsPrevious.addEventListener('click', () => {
            if(this.#blockPlayback)
            {
                this.#blockPlayback.previous?.play();
            }
            else if(this.#blockFocus)
            {
                this.#blockFocus.previous?.scrollHere();
            }
            else
            {
                this.#sections[0].blocks[0].scrollHere();
            }
        });

        this.#elementMasterControlsNext = domMasterControls.next._element;
        this.#elementMasterControlsNext.addEventListener('click', () => {
            if(this.#blockPlayback)
            {
                this.#blockPlayback.next?.play();
            }
            else if(this.#blockFocus)
            {
                this.#blockFocus.next?.scrollHere();
            }
            else
            {
                this.#sections[0].blocks[0].scrollHere();
            }
        });

        if(this.#sections.length > 0)
        {
            const sectionFirst = this.#sections[0];
            sectionFirst.updateMedia();
            if(sectionFirst.blocksFlattened.length > 0)
            {
                this.#blockFocus = sectionFirst.blocksFlattened[0];
            }
        }

        this.#scrollTranslator = new XScrollTranslator((scrollTop, sectionID, position) =>
        {
            this.#element.scrollTop = scrollTop;
            if(sectionID >= 0)
            {
                this.#sections[sectionID].onScroll(position);
            }
            else
            {
                // Negative number is a transition between sectionID-1 and sectionID
                sectionID = -sectionID;
                this.#sections[sectionID-1].onScroll(position);
                if(sectionID < this.#sections.length)
                {
                    this.#sections[sectionID].onScroll(position);
                }
            }
        });

        this.masterVolume = parseFloat(window.localStorage.getItem('masterVolume') ?? 0.5);
    };

    #updateMasterControls()
    {
        this.#elementMasterControlsPrevious.classList.add('disabled');
        this.#elementMasterControlsNext.classList.add('disabled');
        this.#elementMasterControlsPlay.classList.remove('x-playing');
        this.#elementMasterControlsPlayIcon.classList.add('bi-play-fill');
        this.#elementMasterControlsPlayIcon.classList.remove('bi-stop-fill');
        if(this.#blockPlayback)
        {
            this.#elementMasterControlsPlay.classList.add('x-playing');
            this.#elementMasterControlsPlayIcon.classList.remove('bi-play-fill');
            this.#elementMasterControlsPlayIcon.classList.add('bi-stop-fill');

            if(this.#blockPlayback.previous)
            {
                this.#elementMasterControlsPrevious.classList.remove('disabled');
            }
            if(this.#blockPlayback.next)
            {
                this.#elementMasterControlsNext.classList.remove('disabled');
            }
        }
        else if(this.#blockFocus)
        {
            if(this.#blockFocus.previous)
            {
                this.#elementMasterControlsPrevious.classList.remove('disabled');
            }
            if(this.#blockFocus.next)
            {
                this.#elementMasterControlsNext.classList.remove('disabled');
            }
        }
    };

    set masterVolume(volume)
    {
        this.#masterVolume = volume;
        const volumePercentInteger = Math.round(this.#masterVolume * 100.0);
        this.#elementMasterControlsVolumeSlider.value = this.#masterVolume;
        this.#elementMasterControlsVolumeLabel.innerText = String(volumePercentInteger);
        this.#elementMasterControlsVolumeSlider.style.setProperty('--range-percent', String(volumePercentInteger) + '%');
        if(this.#blockPlayback)
        {
            this.#blockPlayback.volume = volume;
        }
        window.localStorage.setItem('masterVolume', String(this.#masterVolume));
    }

    onResize()
    {
        this.#blockPlayback?.stop();

        this.#element.scrollTop = 0;
        this.#element.style.maxWidth = String(this.#page.maxContentWidth) + 'px';

        if(this.#page.windowWidth < 992 && this.#page.maxContentWidth == this.#page.windowWidth)
        {
            this.#element.classList.add('x-full-width');
        }
        else
        {
            this.#element.classList.remove('x-full-width');
        }
        let currentSourceScroll = 0;
        let previousSectionTop = 0;
        this.#scrollTranslator.reset();
        for(let i = 0; i < this.#sections.length; i++)
        {
            const section = this.#sections[i];
            const sectionTop = section.element.getBoundingClientRect().top - this.#page.navbarHeight;
            currentSourceScroll += sectionTop - previousSectionTop;
            previousSectionTop = sectionTop;
            this.#scrollTranslator.addBreakpoint(currentSourceScroll, sectionTop, i);
            currentSourceScroll = section.onResize(currentSourceScroll);
            this.#scrollTranslator.addBreakpoint(currentSourceScroll, sectionTop, -(i+1));
        }

        return currentSourceScroll;
    }

    onScroll(position)
    {
        for(const section of this.#sections)
        {
            for(const block of section.blocksFlattened)
            {
                if(block.elementAnchor)
                {
                    block.elementAnchor.classList.remove('x-highlight');
                }
            }
        }

        this.#scrollTranslator.onScroll(position);
    };

    set blockPlayback(blockPlayback)
    { 
        this.#blockPlayback = blockPlayback;
        this.#updateMasterControls();
    }

    set blockFocus(blockFocus)
    {
        this.#blockFocus = blockFocus;
        this.#updateMasterControls();
    }

    get _dom          () { return this.#dom;           }
    get page          () { return this.#page;          }
    get site          () { return this.#site;          }
    get sections      () { return this.#sections;      }
    get element       () { return this.#element;       }
    get masterVolume  () { return this.#masterVolume;  }
    get blockPlayback () { return this.#blockPlayback; }
    get blockFocus    () { return this.#blockFocus;    }
};

export class XContentReactive extends XContent
{
    static get specification () 
    {
        return xObject({
            $: XContent.type,
            settings: xObject({
                name: xString()
            }),
            sections: xArray(xReference('XSection'))
        });
    }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XContentReactive.specification, domEnvironment);
    }

    #data;

    constructor(data, page, site)
    {
        super(XContentReactive.#template._createAccessibleDOM(data), page, site);
        this.#data = data;
    };

    collectStaticStorageIDs(collectedIDs)
    {
        for(const section of this.sections)
        {
            section.collectStaticStorageIDs(collectedIDs);
        }
    }

    get data () { return this.#data; }
};
